import waRequest from './wa-request';

export const apiBase = '/incident-management/v1';

function createIncident({data, signal}) {
  const url = `${apiBase}/incidents`;

  return waRequest.requestAuthenticated({
    url,
    method: 'post',
    signal,
    data,
  });
}

function searchIncidents({params = {}, data = {}, signal}) {
  const {page, size, sort} = params;
  const url = `${apiBase}/incidents/searches`;

  return waRequest.requestAuthenticated({
    url,
    method: 'post',
    params: {
      page,
      size,
      sort,
    },
    data,
    signal,
  });
}

function getIncident({pathParams: {incidentId}, params: {organizationId} = {}, signal}) {
  const url = `${apiBase}/incidents/${incidentId}`;

  return waRequest.requestAuthenticated({
    url,
    method: 'get',
    signal,
    params: {
      organizationId,
    },
  });
}

function getIncidentTimeline({pathParams: {incidentId}, params: {organizationId, page, size, sort} = {}, signal}) {
  const url = `${apiBase}/incidents/${incidentId}/timeline`;

  return waRequest.requestAuthenticated({
    url,
    method: 'get',
    signal,
    params: {
      organizationId,
      page,
      size,
      sort,
    },
  });
}

function addIncidentComment({pathParams: {incidentId}, params: {organizationId} = {}, data, signal}) {
  const url = `${apiBase}/incidents/${incidentId}/comments`;

  return waRequest.requestAuthenticated({
    url,
    method: 'post',
    signal,
    params: {
      organizationId,
    },
    data,
  });
}

function listIncidentTasks({pathParams: {incidentId}, params: {organizationId} = {}, signal}) {
  const url = `${apiBase}/incidents/${incidentId}/tasks`;

  return waRequest.requestAuthenticated({
    url,
    method: 'get',
    signal,
    params: {
      organizationId,
    },
  });
}

function addIncidentTask({pathParams: {incidentId}, params: {organizationId} = {}, data, signal}) {
  const url = `${apiBase}/incidents/${incidentId}/tasks`;

  return waRequest.requestAuthenticated({
    url,
    method: 'post',
    signal,
    params: {
      organizationId,
    },
    data,
  });
}

function updateIncidentTask({pathParams: {incidentId, taskId}, params: {organizationId} = {}, data, signal}) {
  const url = `${apiBase}/incidents/${incidentId}/tasks/${taskId}`;

  return waRequest.requestAuthenticated({
    url,
    method: 'patch',
    signal,
    params: {
      organizationId,
    },
    data,
  });
}

function deleteIncidentTask({pathParams: {incidentId, taskId}, params: {organizationId} = {}, signal}) {
  const url = `${apiBase}/incidents/${incidentId}/tasks/${taskId}`;

  return waRequest.requestAuthenticated({
    url,
    method: 'delete',
    signal,
    params: {
      organizationId,
    },
  });
}

function updateIncident({pathParams: {incidentId}, params: {organizationId} = {}, data, signal}) {
  const url = `${apiBase}/incidents/${incidentId}`;

  return waRequest.requestAuthenticated({
    url,
    method: 'patch',
    signal,
    params: {
      organizationId,
    },
    data,
  });
}

function getIncidentConfiguration({pathParams: {incidentId}, params: {organizationId} = {}, signal}) {
  const url = `${apiBase}/incidents/${incidentId}/configurations`;

  return waRequest.requestAuthenticated({
    url,
    method: 'get',
    signal,
    params: {
      organizationId,
    },
  });
}

function addIncidentResponders({pathParams: {incidentId}, params: {organizationId} = {}, data, signal}) {
  const url = `${apiBase}/incidents/${incidentId}/responders`;

  return waRequest.requestAuthenticated({
    url,
    method: 'post',
    signal,
    params: {
      organizationId,
    },
    data,
  });
}

function removeIncidentResponder({pathParams: {incidentId, responderUserId}, params: {organizationId} = {}, signal}) {
  const url = `${apiBase}/incidents/${incidentId}/responders/${responderUserId}`;

  return waRequest.requestAuthenticated({
    url,
    method: 'delete',
    signal,
    params: {
      organizationId,
    },
  });
}

function getIncidentById({pathParams: {incidentId}, params: {organizationId} = {}, signal}) {
  return getIncident({pathParams: {incidentId}, params: {organizationId}, signal});
}

function getOrganizationConfiguration({params: {organizationId} = {}, signal}) {
  const url = `${apiBase}/configurations`;

  return waRequest.requestAuthenticated({
    url,
    method: 'get',
    signal,
    params: {
      organizationId,
    },
  });
}

// The organization's default incident statuses, task statuses and priority labels are each replaced with a PUT and
// deleted (going back to the system default) with a DELETE
function replaceOrganizationConfigurationPart(part, {params: {organizationId} = {}, data, signal}) {
  const url = `${apiBase}/configurations/${part}`;

  return waRequest.requestAuthenticated({
    url,
    method: 'put',
    signal,
    params: {
      organizationId,
    },
    data,
  });
}

function deleteOrganizationConfigurationPart(part, {params: {organizationId} = {}, signal}) {
  const url = `${apiBase}/configurations/${part}`;

  return waRequest.requestAuthenticated({
    url,
    method: 'delete',
    signal,
    params: {
      organizationId,
    },
  });
}

export const ORGANIZATION_CONFIGURATION_PARTS = {
  INCIDENT_STATUSES: 'incident-statuses',
  TASK_STATUSES: 'task-statuses',
  PRIORITY_LABELS: 'priority-labels',
};

function replaceOrganizationIncidentStatuses(config) {
  return replaceOrganizationConfigurationPart(ORGANIZATION_CONFIGURATION_PARTS.INCIDENT_STATUSES, config);
}

function deleteOrganizationIncidentStatuses(config) {
  return deleteOrganizationConfigurationPart(ORGANIZATION_CONFIGURATION_PARTS.INCIDENT_STATUSES, config);
}

function replaceOrganizationTaskStatuses(config) {
  return replaceOrganizationConfigurationPart(ORGANIZATION_CONFIGURATION_PARTS.TASK_STATUSES, config);
}

function deleteOrganizationTaskStatuses(config) {
  return deleteOrganizationConfigurationPart(ORGANIZATION_CONFIGURATION_PARTS.TASK_STATUSES, config);
}

function replaceOrganizationPriorityLabels(config) {
  return replaceOrganizationConfigurationPart(ORGANIZATION_CONFIGURATION_PARTS.PRIORITY_LABELS, config);
}

function deleteOrganizationPriorityLabels(config) {
  return deleteOrganizationConfigurationPart(ORGANIZATION_CONFIGURATION_PARTS.PRIORITY_LABELS, config);
}

// Incident types are added and changed one at a time, since incidents and Response Plans refer to them by ID. Both
// calls resolve with the organization's updated configuration
function addOrganizationIncidentType({params: {organizationId} = {}, data, signal}) {
  const url = `${apiBase}/configurations/incident-types`;

  return waRequest.requestAuthenticated({
    url,
    method: 'post',
    signal,
    params: {
      organizationId,
    },
    data,
  });
}

function updateOrganizationIncidentType({pathParams: {incidentTypeId}, params: {organizationId} = {}, data, signal}) {
  const url = `${apiBase}/configurations/incident-types/${incidentTypeId}`;

  return waRequest.requestAuthenticated({
    url,
    method: 'patch',
    signal,
    params: {
      organizationId,
    },
    data,
  });
}

export default {
  createIncident,
  searchIncidents,
  getIncident,
  getIncidentById,
  updateIncident,
  getIncidentConfiguration,
  addIncidentResponders,
  removeIncidentResponder,
  getIncidentTimeline,
  addIncidentComment,
  listIncidentTasks,
  addIncidentTask,
  updateIncidentTask,
  deleteIncidentTask,
  getOrganizationConfiguration,
  replaceOrganizationIncidentStatuses,
  deleteOrganizationIncidentStatuses,
  replaceOrganizationTaskStatuses,
  deleteOrganizationTaskStatuses,
  replaceOrganizationPriorityLabels,
  deleteOrganizationPriorityLabels,
  addOrganizationIncidentType,
  updateOrganizationIncidentType,
};
